import { useCallback, useMemo } from 'react';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { RecordFolder } from 'src/graphql/entities/record-folder/record-folder.types';
import {
  useCreateUserSettingValue,
  UserSettingId,
  useUpdateUserSettingValue,
} from 'src/graphql/entities/user-setting-value';

export const useRecordFolderVisibility = () => {
  const { user } = useCurrentUserProfile();

  const existingHiddenValue = useMemo(
    () => user?.userSettingValueMap.get(UserSettingId.HiddenRecordFolders),
    [user?.userSettingValueMap],
  );

  const { createUserSettingValue } = useCreateUserSettingValue();
  const { updateUserSettingValue } = useUpdateUserSettingValue();

  const hideRecordFolder = useCallback(
    (recordFolder: RecordFolder) => {
      if (existingHiddenValue) {
        const ids = [
          ...new Set([
            recordFolder.recordFolderId,
            ...((existingHiddenValue.value ?? []) as string[]),
          ]),
        ];
        updateUserSettingValue(existingHiddenValue.userSettingValue, {
          value: ids.join(','),
        });
      } else {
        createUserSettingValue({
          userSettingId: UserSettingId.HiddenRecordFolders,
          value: [recordFolder.recordFolderId].join(','),
          valueType: 'string[]',
        });
      }
    },
    [createUserSettingValue, existingHiddenValue, updateUserSettingValue],
  );

  const showRecordFolder = useCallback(
    (recordFolder: RecordFolder) => {
      if (!existingHiddenValue) {
        return;
      }
      const ids = ((existingHiddenValue.value ?? []) as string[]).filter(
        (i) => i !== recordFolder.recordFolderId,
      );
      updateUserSettingValue(existingHiddenValue.userSettingValue, {
        value: ids.join(','),
      });
    },
    [existingHiddenValue, updateUserSettingValue],
  );

  return {
    hideRecordFolder,
    showRecordFolder,
  };
};
